import { useState, type CSSProperties, type ReactNode } from "react";
import { AtSign } from "lucide-react";
import { RelativeLabel } from "@nurion/ui/relative-label";

import { EmptyState } from "~/components/EmptyState";
import { PersonAvatar } from "~/components/PersonAvatar";
import { useMatrix } from "~/context/MatrixContext";
import { useToast } from "~/context/ToastContext";
import { useActivityFeed, type ActivityRow } from "~/hooks/useAttention";
import { useMentionsFeed } from "~/hooks/useMentionsFeed";
import { useThreadsFeed } from "~/hooks/useThreadsFeed";
import { plainPreview } from "~/lib/message-utils";
import { absoluteFallback, LOCAL_ZONE } from "~/lib/relative-time";
import { ICON_STROKE } from "~/lib/icon";

const ROW_STYLE_ID = "chat-activity-row-styles";

function injectRowStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(ROW_STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = ROW_STYLE_ID;
  style.textContent = `
    .chat-activity-row:hover { background: var(--chat-surface-raised); }
    @keyframes chat-activity-shimmer-pulse {
      0%, 100% { background-color: var(--chat-surface-raised); }
      50% { background-color: var(--chat-surface-overlay); }
    }
    .chat-activity-shimmer {
      animation: chat-activity-shimmer-pulse var(--chat-motion-slow) var(--chat-motion-ease) infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .chat-activity-shimmer { animation: none; }
    }
  `;
  document.head.appendChild(style);
}
injectRowStyles();

export function MentionsView() {
  const rows = useActivityFeed();
  const { clearAll } = useMentionsFeed();
  const { markAllRead } = useThreadsFeed();
  const { jumpToEvent, state } = useMatrix();
  const { push: pushToast } = useToast();
  const [clearing, setClearing] = useState(false);

  const unread = rows.filter((row) => row.unread).length;

  const handleClear = async () => {
    try {
      setClearing(true);
      await Promise.all([clearAll(), markAllRead()]);
      pushToast({ message: "Marked all read." });
    } catch {
      pushToast({ message: "Couldn't mark activity as read.", tone: "danger" });
    } finally {
      setClearing(false);
    }
  };

  if (state === "loading" || state === "syncing") {
    return (
      <ViewShell title="Activity">
        <ActivityShimmer />
      </ViewShell>
    );
  }

  const action =
    rows.length > 0
      ? { label: clearing ? "Marking…" : "Mark all read", onClick: handleClear, disabled: clearing }
      : null;

  return (
    <ViewShell title="Activity" count={rows.length > 0 ? unread : undefined} action={action}>
      {rows.length === 0 ? (
        <EmptyState
          icon={<ActivityIllustration />}
          primary="No activity yet."
          secondary="Mentions, reactions and thread replies will appear here."
        />
      ) : (
        <ul
          aria-live="assertive"
          style={{ flex: 1, overflowY: "auto", listStyle: "none", margin: 0, padding: 0 }}
        >
          {rows.map((row) => (
            <ActivityRow
              key={row.id}
              row={row}
              onJump={() => jumpToEvent({ roomId: row.roomId, eventId: jumpTarget(row) })}
            />
          ))}
        </ul>
      )}
    </ViewShell>
  );
}

function jumpTarget(row: ActivityRow): string {
  if (row.kind === "reaction") return row.targetEventId;
  return row.id;
}

function ViewShell({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  action?: { label: string; onClick: () => void; disabled?: boolean } | null;
  children: ReactNode;
}) {
  return (
    <section
      data-testid="activity-view"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        height: "100%",
        background: "var(--chat-surface-app)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
      }}
    >
      <Header title={title} count={count} action={action ?? null} />
      {children}
    </section>
  );
}

function Header({
  title,
  count,
  action,
}: {
  title: string;
  count?: number;
  action: { label: string; onClick: () => void; disabled?: boolean } | null;
}) {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: "var(--chat-timeline-room-header-height)",
        padding: "0 var(--chat-space-4)",
        borderBottom: "1px solid var(--chat-surface-border)",
        flexShrink: 0,
      }}
    >
      <AtSign size={16} strokeWidth={ICON_STROKE} style={{ color: "var(--chat-fg-secondary)" }} />
      <h2
        style={{
          flex: 1,
          margin: 0,
          color: "var(--chat-fg-primary)",
          fontFamily: "var(--chat-font-display)",
          fontSize: "var(--chat-type-display-sm-size)",
          lineHeight: "var(--chat-type-display-sm-line)",
          fontWeight: 300,
        }}
      >
        {title}
        {count !== undefined && (
          <span
            style={{
              marginLeft: "var(--chat-space-2)",
              color: "var(--chat-fg-secondary)",
              fontFamily: "var(--chat-font-sans)",
              fontSize: "var(--chat-type-body-md-size)",
              lineHeight: "var(--chat-type-body-md-line)",
              fontWeight: 400,
            }}
          >
            · {count}
          </span>
        )}
      </h2>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          disabled={action.disabled}
          className="chat-button"
          style={ghostButtonStyle}
        >
          {action.label}
        </button>
      )}
    </header>
  );
}

function ActivityRow({ row, onJump }: { row: ActivityRow; onJump: () => void }) {
  const { client } = useMatrix();
  const senderName = row.kind === "thread" ? row.sender : row.sender.displayName || row.sender.id;
  const avatarUrl = row.kind === "thread" ? null : row.sender.avatarUrl;
  const secondary = secondaryLabel(row);
  const snippet = snippetText(row);
  return (
    <li
      className="chat-activity-row"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: 64,
        padding: "0 var(--chat-space-4)",
      }}
    >
      {row.unread && <span className="chat-activity-dot" aria-hidden style={dotStyle} />}
      <button
        type="button"
        onClick={onJump}
        aria-label={`Jump to ${senderName} in ${row.roomName}${row.unread ? ", unread" : ""}`}
        style={{
          ...(row.unread ? {} : { opacity: 0.6 }),
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-3)",
          border: "none",
          background: "transparent",
          color: "inherit",
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <PersonAvatar client={client} avatarUrl={avatarUrl} name={senderName} size={24} />
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "var(--chat-space-2)" }}>
            <span style={senderStyle}>{senderName}</span>
            {secondary && <span style={secondaryStyle}>{secondary}</span>}
            <RoomChip name={row.roomName} />
            <span style={timeStyle}>
              <RelativeLabel
                value={new Date(row.ts).toISOString()}
                zone={LOCAL_ZONE}
                fallback={absoluteFallback(row.ts)}
              />
            </span>
          </div>
          {snippet !== "" && (
            <div style={snippetStyle}>
              <span style={snippetTextStyle}>{snippet}</span>
            </div>
          )}
        </div>
      </button>
    </li>
  );
}

function secondaryLabel(row: ActivityRow): string {
  if (row.kind === "thread") return "replied in thread";
  if (row.kind === "reaction") return "reacted";
  return "";
}

function snippetText(row: ActivityRow): string {
  if (row.kind === "mention") return plainPreview(row.body);
  if (row.kind === "reaction") return row.emoji;
  return plainPreview(row.preview);
}

function RoomChip({ name }: { name: string }) {
  return <span style={roomChipStyle}>{name}</span>;
}

function ActivityIllustration() {
  return (
    <svg
      aria-hidden
      width={80}
      height={80}
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx={40}
        cy={40}
        r={32}
        stroke="var(--chat-surface-raised)"
        strokeWidth={ICON_STROKE}
        fill="var(--chat-surface-panel)"
      />
      <g
        data-testid="activity-empty-glyph"
        transform="translate(24 24) scale(1.3333)"
        stroke="var(--chat-fg-muted)"
        strokeWidth={1.125}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        <circle cx={12} cy={12} r={4} />
        <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" />
      </g>
    </svg>
  );
}

function ActivityShimmer() {
  return (
    <div
      aria-hidden
      style={{
        flex: 1,
        padding: "var(--chat-space-2)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--chat-space-1)",
      }}
    >
      {[0, 1, 2, 3, 4].map((index) => (
        <div
          key={index}
          className="chat-activity-shimmer"
          style={{ height: 64, borderRadius: "var(--chat-radius-sm)" }}
        />
      ))}
    </div>
  );
}

const ghostButtonStyle: CSSProperties = {
  padding: "var(--chat-space-1) var(--chat-space-3)",
  borderRadius: "var(--chat-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  fontWeight: 500,
  cursor: "pointer",
};

const dotStyle: CSSProperties = {
  width: 6,
  height: 6,
  flexShrink: 0,
  borderRadius: "var(--chat-radius-full)",
  background: "var(--chat-accent)",
};

const senderStyle: CSSProperties = {
  color: "var(--chat-fg-primary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-md-size)",
  lineHeight: "var(--chat-type-body-md-line)",
  fontWeight: 400,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const secondaryStyle: CSSProperties = {
  color: "var(--chat-fg-muted)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "var(--chat-type-body-xs-line)",
  flexShrink: 0,
};

const timeStyle: CSSProperties = {
  marginLeft: "auto",
  color: "var(--chat-fg-muted)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "var(--chat-type-body-xs-line)",
  flexShrink: 0,
};

const snippetStyle: CSSProperties = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const snippetTextStyle: CSSProperties = {
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
};

const roomChipStyle: CSSProperties = {
  padding: "0 var(--chat-space-1)",
  borderRadius: "var(--chat-radius-sm)",
  background: "var(--chat-surface-raised)",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-xs-size)",
  lineHeight: "var(--chat-type-body-xs-line)",
  flexShrink: 0,
};
